<script>
import Card from '../../../Components/Card.vue';
import Label from '../../../Components/Inputs/Label.vue';
import Textarea from '../../../Components/Inputs/Textarea.vue';
import Loading from '../../../Components/Loading.vue';
import { genericError } from '../../../Modules/Alerts.js';
import { formatNumberAsMoney } from '../../../Modules/Money.js';
import SignaturePad from '../../../Components/Inputs/SignaturePad.vue';
import { formatPhone } from '@/Modules/Phones.js';

export default {
  components: { SignaturePad, Loading, Textarea, Label, Card },

  props: {
    masterConfig: Object,
    kioskKey: String,
    form: Object,
    agreed: Boolean,
    deferredServices: Object,
  },

  emits: ['ready', 'update', 'preReady'],

  data() {
    return {
      settingsLoading: false,
      questionnaire: [],
      questionsLoading: false,
      openNotes: [],
      settings: {},
      checked: false,
    };
  },

  computed: {
    filteredQuestions() {
      let questionsaire = this.questionnaire.filter((question) => {
        return question.ask_scheduler === true;
      });

      if (this.form.customer !== null) {
        //Filter Questions for new only
        return questionsaire.filter((question) => {
          return question.only_new_customer == false;
        });
      }
      return questionsaire;
    },

    isSignatureRequired() {
      let signature_required = false;
      if (this.settings?.slot_settings?.signature_pad !== undefined) {
        signature_required = this.settings.slot_settings.signature_pad;
      }
      return signature_required;
    },

    isDisclaimerRequired() {
      if (
        this.settings?.slot_settings?.disclaimer_text !== undefined &&
        this.settings?.slot_settings?.disclaimer_text?.length > 0
      ) {
        return true;
      }
      return false;
    },

    isReadyToSubmit() {
      let questionsRequired = this.filteredQuestions.filter((question) => {
        return question.required == true;
      });

      let signature_required = this.isSignatureRequired;
      let questions = questionsRequired.filter((question) => {
        const answer = this.form?.questionaire[question.q_id]?.answer;
        return answer === undefined || answer == null || answer === '';
      });
      let disclaimer_required = this.isDisclaimerRequired;
      let is_ready = true;

      if (signature_required === true) {
        if (this.form.signature === null) {
          is_ready = false;
        }
      }

      if (questionsRequired) {
        if (questions.length > 0) {
          is_ready = false;
        }
      }

      if (disclaimer_required) {
        if (this.checked === false) {
          is_ready = false;
        }
      }

      return is_ready;
    },

    customerName() {
      if (this.form.newCustomer.firstname !== null) {
        return this.form.newCustomer.firstname + ' ' + this.form.newCustomer.lastname;
      }
    },

    phoneNumber() {
      let phone = '';
      if (this.form.newCustomer.firstname !== null) {
        phone = this.form.newCustomer.phone;
      } else {
        phone = '';
      }

      if (phone === null) {
        phone = '';
      }

      const digits = phone.replace(/\D/g, ''); // remove all non-digit characters
      if (digits.length !== 10) {
        return phone; // basic validation
      }
      if (this.form.newCustomer.phone === phone) {
        return formatPhone(phone);
      }
      const lastFour = digits.slice(-4);
      return `(xxx) xxx-${lastFour}`;
    },

    email() {
      let email = '';
      if (this.form.newCustomer.firstname !== null) {
        email = this.form.newCustomer.email;
      } else {
        email = '';
      }
      return email;
    },

    vehicleName() {
      if (this.form.newVehicle.make !== null) {
        return (
          this.form.newVehicle.year +
          ' ' +
          this.form.newVehicle.make +
          ' ' +
          this.form.newVehicle.model
        );
      }
      return this.form.vehicle.full_vehicle;
    },

    appointmentTime() {
      if (this.form.appointment === undefined || this.form.appointment === null) {
        return '--';
      }
      const dateStr = this.form.appointment.slice(0, 19); // "2025-09-05T09:00:00"
      const date = new Date(dateStr);

      const dateFormatted = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeFormatted = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      return `${dateFormatted} @ ${timeFormatted}`;
    },

    totalCost() {
      let total = 0;

      Object.keys(this.form.services).forEach((key) => {
        let service = this.form.services[key];
        if (service.price !== null && service.price !== '') {
          total += parseFloat(service.price);
        }
      });

      this.tcTires.forEach((tire) => {
        total += parseFloat(tire.price || 0) * (parseInt(tire.quantity) || 1);
      });
      this.tcServices.forEach((svc) => {
        total += parseFloat(svc.total_price || svc.price || 0);
      });
      total += parseFloat(this.tcTax || 0);

      return total;
    },

    totalCostFormatted() {
      return formatNumberAsMoney(this.totalCost);
    },

    hasDeferredServices() {
      return (
        this.deferredServices &&
        Array.isArray(this.deferredServices) &&
        this.deferredServices.length > 0
      );
    },

    tcTires() {
      if (!this.form?.tireConnectData) return [];
      return this.form.tireConnectData.quote?.tires || this.form.tireConnectData.tires || [];
    },

    tcServices() {
      if (!this.form?.tireConnectData) return [];
      return this.form.tireConnectData.quote?.services || this.form.tireConnectData.services || [];
    },

    tcTax() {
      if (!this.form?.tireConnectData) return 0;
      return this.form.tireConnectData.quote?.tax || this.form.tireConnectData.total_tax || 0;
    },

    hasTcData() {
      return this.tcTires.length > 0 || this.tcServices.length > 0;
    },
  },

  watch: {
    isReadyToSubmit(newValue) {
      if (this.settingsLoading || this.questionsLoading) return;
      this.$emit('ready', newValue);
      this.$emit('preReady', !newValue ? this.scrollToFirstError : null);
    },
  },

  mounted() {
    this.getQuestions();
    this.getSettings();
    this.checked = this.agreed;
  },

  methods: {
    checkReadyState() {
      if (this.settingsLoading || this.questionsLoading) return;
      this.$emit('ready', this.isReadyToSubmit);
      this.$emit('preReady', !this.isReadyToSubmit ? this.scrollToFirstError : null);
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        let container = null;

        // Check for unanswered required questions first
        const unansweredQuestion = this.filteredQuestions.find((q) => {
          if (!q.required) return false;
          const answer = this.form?.questionaire[q.q_id]?.answer;
          return answer === undefined || answer === null || answer === '';
        });

        if (unansweredQuestion) {
          // Find the textarea for this question by scanning form-group labels
          const formGroups = this.$el.querySelectorAll('.form-group');
          for (const group of formGroups) {
            if (group.textContent.includes(unansweredQuestion.question)) {
              container = group;
              break;
            }
          }
        }

        // Check signature
        if (!container && this.isSignatureRequired && !this.form.signature) {
          container =
            this.$el.querySelector('.signature-pad') ??
            this.$el.querySelector('.form-group:has(canvas)');
        }

        // Check disclaimer
        if (!container && this.isDisclaimerRequired && !this.checked) {
          container = this.$el.querySelector('.disclaimer-container');
        }

        if (!container) return;

        const headerHeight = 60;
        const top = container.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });

        // Only pulse the disclaimer container, not arbitrary elements
        if (container.classList.contains('disclaimer-container')) {
          setTimeout(() => {
            container.classList.add('disclaimer-error');
            setTimeout(() => container.classList.remove('disclaimer-error'), 2000);
          }, 300);
        }
      });
    },

    formatNumberAsMoney,

    toggleCheck() {
      this.checked = !this.checked;
      this.$emit('update', { checked_disclaimer: this.checked });
    },

    getSettings() {
      this.settingsLoading = true;
      axios
        .post(route('scheduler.settings', { key: this.kioskKey }))
        .then((response) => {
          this.settings = response.data.data;
          if (!this.isReadyToSubmit) {
            this.$emit('preReady', this.scrollToFirstError);
          }
        })
        .finally(() => {
          this.settingsLoading = false;
          this.checkReadyState();
        });
    },

    updateAnswer(question, answer) {
      question.answer = answer;
      this.$emit('update', {
        question: question,
      });
    },

    updateSignature(signature) {
      this.$emit('update', {
        signature: signature,
      });
    },
    getQuestions() {
      this.questionsLoading = true;
      axios
        .post(
          route('scheduler.questions', {
            key: this.kioskKey,
          }),
        )
        .then((response) => {
          this.questionnaire = response.data.data.questions;
        })
        .catch((error) => {
          genericError();
        })
        .finally(() => {
          this.questionsLoading = false;
          this.checkReadyState();
        });
    },

    addNote(service, note) {
      service.note = note;
      this.$emit('update', {
        service: service,
      });
    },

    openNote(service) {
      this.openNotes.push(service.kiosk_job_id);
    },
  },
};
</script>

<template>
  <Card class="mt-3">
    <!-- Customer Info -->
    <h4>Customer Info</h4>
    <ul class="list-unstyled mb-4">
      <li class="mb-2"><i class="fas fa-user mr-2" /> {{ customerName }}</li>
      <li v-if="phoneNumber" class="mb-2"><i class="fas fa-phone mr-2" /> {{ phoneNumber }}</li>
      <li v-if="email !== null" class="mb-2"><i class="fas fa-envelope mr-2" /> {{ email }}</li>
      <li class="mb-2"><i class="fas fa-car mr-2" /> {{ vehicleName }}</li>
      <li class="mb-2">
        <i class="fas fa-calendar-alt mr-2" />
        <strong class="ms-1 fs-4">{{ appointmentTime }}</strong>
      </li>
      <li>
        <i class="fa fa-clock mr-2"></i>
        {{ form?.preferences?.appointmentType == 'd' ? 'Drop Off' : '' }}
        {{ form?.preferences?.appointmentType == 'w' ? 'Waiting' : '' }}
      </li>
    </ul>

    <Loading v-if="questionsLoading" />

    <template v-if="filteredQuestions.length > 0">
      <h4 class="mb-3">Questionnaire</h4>

      <template v-for="question in filteredQuestions">
        <div class="form-group mb-3">
          <Label :required="false"
            ><span class="text-danger" v-if="question.required">(Required)</span>
            {{ question.question }}</Label
          >
          <Textarea
            :model-value="form?.questionaire[question.q_id]?.answer || null"
            :required="question.required"
            @update:keyup="(newValue) => updateAnswer(question, newValue)"
          />
        </div>
      </template>
    </template>

    <!-- Visit Details -->
    <h4 class="mb-3">Visit Details</h4>

    <template v-for="service in form.services">
      <div class="mb-3 d-flex justify-content-between align-items-center">
        <div>{{ service.job_name }}</div>
        <div>{{ service.price > 0 ? formatNumberAsMoney(service.price) : '' }}</div>
      </div>
      <button
        v-if="!openNotes.includes(service.kiosk_job_id)"
        class="btn btn-outline-primary btn-sm mb-4"
        @click="openNote(service)"
      >
        Add Note <span class="ml-1">+</span>
      </button>
      <Textarea
        v-else
        :model-value="service?.note || null"
        @update:keyup="(newValue) => addNote(service, newValue)"
      />
    </template>

    <template v-if="hasTcData">
      <template v-for="(tire, index) in tcTires" :key="'tire-' + index">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <div>
              ({{ tire.quantity }}) {{ tire.brand_name || tire.brand }} -
              {{ tire.model_name || tire.model }}
            </div>
            <div class="text-muted small">
              {{ tire.category?.label || '' }} {{ tire.size }} Part# {{ tire.part_number }}
            </div>
          </div>
          <div>{{ tire.price > 0 ? formatNumberAsMoney(tire.price * tire.quantity) : '' }}</div>
        </div>
      </template>
      <template v-for="(svc, index) in tcServices" :key="'svc-' + index">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <!-- Fallback to svc.key if svc.name is blank -->
          <div>{{ svc.name || svc.key || 'Service' }}</div>
          <div>
            {{
              (svc.total_price || svc.price) > 0
                ? formatNumberAsMoney(svc.total_price || svc.price)
                : ''
            }}
          </div>
        </div>
      </template>
    </template>

    <template
      v-if="form?.recommendations !== undefined && Object.keys(form.recommendations).length > 0"
    >
      <h4 class="mb-3">Previous Recommendations</h4>
      <template v-for="recommendation in form.recommendations">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <div>{{ recommendation?.item.inspec_name }}</div>
          <div>$TBD</div>
        </div>
      </template>
    </template>

    <template v-if="hasDeferredServices">
      <h4 class="mb-3 mt-4">Previously Declined Services</h4>
      <template v-for="service in deferredServices" :key="service.history_job_id">
        <div class="mb-3 p-3 border rounded bg-light">
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <strong>{{ service.job_name }}</strong>
              <p class="mb-1 text-muted small">{{ service.job_description }}</p>
              <small class="text-muted">
                From visit on
                {{
                  new Date(service.insert_datetime).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                }}
              </small>
            </div>
          </div>
        </div>
      </template>
    </template>

    <!-- Sales Tax -->
    <div v-if="totalCost > 0" class="d-flex justify-content-between align-items-center mt-4">
      <div>
        Estimated Subtotal<br /><span class="note"
          >(Taxes, supplies & other fees not included)</span
        >
      </div>
      <div>{{ totalCostFormatted }}</div>
    </div>

    <template
      v-if="
        settings?.slot_settings?.signature_pad !== undefined &&
        settings.slot_settings.signature_pad === true
      "
    >
      <hr class="mt-3" />
      <h4 class="mb-3">
        <span v-if="isSignatureRequired" class="text-danger">(Required)</span>
        Please Review and Sign Below
      </h4>
      <div class="form-group">
        <SignaturePad
          class="mt-3"
          :model-value="form.signature"
          @update:model-value="(newValue) => updateSignature(newValue)"
        />
      </div>
    </template>

    <template
      v-if="
        settings?.slot_settings?.disclaimer_text &&
        settings?.slot_settings?.disclaimer_text.length > 0
      "
    >
      <div class="d-flex mt-3 flex-column border rounded disclaimer-container">
        <div class="disclaimer-scroll p-3">
          <div class="disclaimer-text" id="disclaimerText">
            {{ settings.slot_settings.disclaimer_text }}
          </div>
        </div>
        <div class="disclaimer-footer d-flex justify-content-end align-items-center px-3 pb-3 pt-2">
          <input
            type="checkbox"
            class="form-check-input custom-checkbox"
            id="disclaimerCheck"
            :checked="checked"
            @change="toggleCheck"
          />
          <label class="form-check-label ms-2" for="disclaimerCheck">I agree</label>
        </div>
      </div>
    </template>
  </Card>
</template>

<style scoped>
.disclaimer-container {
  background-color: #f8f9fa;
  border: 2px solid #dee2e6 !important;
  border-radius: 6px;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.disclaimer-scroll {
  max-height: 180px;
  overflow-y: auto;
  padding-right: 6px;
}

.disclaimer-scroll::-webkit-scrollbar {
  width: 5px;
}
.disclaimer-scroll::-webkit-scrollbar-track {
  background: #e9ecef;
  border-radius: 4px;
}
.disclaimer-scroll::-webkit-scrollbar-thumb {
  background: #adb5bd;
  border-radius: 4px;
}
.disclaimer-scroll::-webkit-scrollbar-thumb:hover {
  background: #868e96;
}

.disclaimer-footer {
  border-top: 1px solid #dee2e6;
  transition: border-color 0.2s ease;
}

.disclaimer-text {
  font-weight: 600;
  color: var(--ink-primary-color);
  white-space: pre-wrap;
}

.custom-checkbox {
  transform: scale(1.5);
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  cursor: pointer;
  flex-shrink: 0;
}

.form-check-label {
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0;
}

@keyframes disclaimer-error-pulse {
  0% {
    border-color: #dc3545;
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5);
  }
  50% {
    border-color: #dc3545;
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    border-color: #dc3545;
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.disclaimer-error {
  border-color: #dc3545 !important;
  background-color: #fff5f5 !important;
  animation: disclaimer-error-pulse 0.6s ease 2;
}

.disclaimer-error .disclaimer-text {
  color: #dc3545;
}
.disclaimer-error .disclaimer-footer {
  border-top-color: #dc3545;
}
.disclaimer-error .form-check-label {
  color: #dc3545;
}
</style>
